import React, { useMemo, useState } from 'react'
import {
  CircleIcon,
  RectangleIcon,
  Chip as SVGChip,
} from '../SVGs'
import ParentMarker from './ParentMarker'
import { useMap } from '../IndoorProvider'
import { LabelContainer, MarkerContainer } from './styles'
import { isRackType } from '../../../../pages/Supervision/helpers'

export const Label = ({ device, chipColor }) => {
  const { iconSize, convertPosRealToMap, transformWrapperState } = useMap()
  const text = useMemo(() => {
    if (device.label.length > 9) {
      return `${device.label.slice(0, 4)}...${device.label.slice(
        device.label.length - 4,
      )}`
    }
    return device.label
  }, [device.label])
  
  const sizes = useMemo(() => {
    const { x, y } = convertPosRealToMap(device.position)
    const textWidth = text.length * 6
    const padding = 16
    const chipSpace = chipColor ? 16 : 0
    const rectWidth = textWidth + padding + chipSpace
    const rectHeight = 20
    const totalWidth = rectWidth + 8
    const totalHeight = 28
    
    return {
      svg: {
        viewBox: `0 0 ${totalWidth} ${totalHeight}`,
        x: x - (totalWidth / 2) / transformWrapperState.scale + 25 / transformWrapperState.scale,
        y: y + (iconSize * 1.1) / transformWrapperState.scale,
        width: totalWidth / transformWrapperState.scale,
        height: totalHeight / transformWrapperState.scale,
      },
      rect: {
        x: 4,
        y: 4,
        width: rectWidth,
        height: rectHeight,
        centerX: totalWidth / 2,
        centerY: totalHeight / 2
      },
      chip: {
        cx: rectWidth - 2,
        cy: 14,
        r: 6,
      },
    }
  }, [device, transformWrapperState.scale, text, chipColor, iconSize, convertPosRealToMap])
  
  return (
    <LabelContainer {...sizes.svg}>
      <rect
        x={sizes.rect.x}
        y={sizes.rect.y}
        width={sizes.rect.width}
        height={sizes.rect.height}
        rx={10}
        ry={10}
        fill="white"
        filter="drop-shadow(1px 2px 2px rgba(0, 0, 0, .16))"
      />
      {Boolean(chipColor) && <circle {...sizes.chip} fill={chipColor} />}
      <text
        x={sizes.rect.centerX}
        y={sizes.rect.centerY}
        fontSize="10px"
        fill="#5D5F70"
        stroke="#5D5F70"
        strokeWidth={0.4}
        textAnchor="middle"
        alignmentBaseline="middle"
      >
        {text}
      </text>
    </LabelContainer>
  )
}

export const Chip = ({ device, pulse = false }) => {
  const { iconSize, convertPosRealToMap, transformWrapperState } = useMap()
  const position = useMemo(() => {
    const { x, y } = convertPosRealToMap(device.position)
    return {
      x: x + (iconSize * 0.65) / transformWrapperState.scale,
      y: y - (iconSize * 0.07) / transformWrapperState.scale,
    }
  }, [device, transformWrapperState.scale, iconSize, convertPosRealToMap])

  return (
    <svg 
      style={{ position: 'absolute', zIndex: 3 }}
      x={position.x}
      y={position.y}
      width={24 / transformWrapperState.scale}
      height={24 / transformWrapperState.scale}
      viewBox="0 0 24 24"
    >
      <SVGChip
        pulse={pulse}
        text={device.connected_stations}
        x={0}
        y={0}
        width={24}
        height={24}
      />
    </svg>
  )
}

const Marker = ({
  device,
  selected,
  onMarkerEnter = () => {},
  onMarkerLeave = () => {},
  onMarkerClick = () => {},
}) => {
  const [hovered, setHovered] = useState(false)
  const { convertPosRealToMap, iconSize, transformWrapperState } = useMap()

  const isRack = useMemo(() => isRackType(device.model?.type), [device.model?.type])
  const hasChildrenOrIsRack = useMemo(
    () => (device.children && device.children.length > 0) || isRack,
    [device.children, isRack]
  )

  if (hasChildrenOrIsRack) {
    return (
      <ParentMarker
        device={device}
        selected={selected}
        onMarkerEnter={onMarkerEnter}
        onMarkerLeave={onMarkerLeave}
        onMarkerClick={onMarkerClick}
      />
    )
  }

  const position = useMemo(() => {
    const { x, y } = convertPosRealToMap(device)
    return { x, y }
  }, [device, convertPosRealToMap])

  const handleMarkerEnter = (id) => {
    setHovered(true)
    onMarkerEnter(id)
  }

  const handleMarkerLeave = () => {
    setHovered(false)
    onMarkerLeave()
  }

  return (
    <MarkerContainer
      key={`${device.uuid}`}
      id={device.uuid}
      onClick={() => onMarkerClick?.(device)}
      viewBox={`0 0 ${iconSize * 2.5} ${iconSize * 2.5}`}
      width={(iconSize / transformWrapperState.scale) * 2.5}
      height={(iconSize / transformWrapperState.scale) * 2.5}
      {...position}
    >
      {device.markers && device.markers.map(
        ({ id, icon, colors, backgroundColor, svgColorFilter, type }) =>
          type === 'ap' ? (
            <CircleIcon
              key={id}
              iconSize={iconSize}
              selected={selected}
              pulse={selected}
              hovered={hovered}
              icon={icon}
              colors={colors}
              onMouseEnter={() => handleMarkerEnter(id)}
              onMouseLeave={handleMarkerLeave}
              id={device.uuid}
            />
          ) : (
            <RectangleIcon
              key={id}
              selected={selected}
              pulse={selected}
              hovered={hovered}
              iconSize={iconSize}
              icon={icon}
              colors={colors}
              svgColorFilter={svgColorFilter}
              onMouseEnter={() => handleMarkerEnter(id)}
              onMouseLeave={handleMarkerLeave}
              id={device.uuid}
            />
          )
      )}
    </MarkerContainer>
  )
}

export default Marker